import { defineStore } from 'pinia';
import pushNotificationService from '@/services/PushNotificationService';

export const useNotificationStore = defineStore('notification', {
  state: () => ({
    notifications: [],
    registrationToken: null,
    isInitialized: false,
    permissionStatus: null,
    error: null,
  }),

  getters: {
    unreadCount: (state) => {
      return state.notifications.filter(notification => notification.unread).length;
    },
    
    unreadNotifications: (state) => {
      return state.notifications.filter(notification => notification.unread);
    },
    
    readNotifications: (state) => {
      return state.notifications.filter(notification => !notification.unread);
    },

    hasPermission: (state) => {
      return state.permissionStatus === 'granted';
    },

    formattedUnreadCount: (state) => {
      const count = state.notifications.filter(notification => notification.unread).length;
      if (count === 0) return null;
      if (count > 9) return '9+';
      return count.toString();
    }
  },

  actions: {
    async initializePushNotifications() {
      try {
        this.error = null;
        
        await pushNotificationService.initialize();
        
        this.isInitialized = true;
        this.registrationToken = pushNotificationService.getRegistrationToken();
        this.notifications = pushNotificationService.getNotifications();
        
        this.setupListeners();
        
        const permissions = await pushNotificationService.checkPermissions();
        this.permissionStatus = permissions.receive;
        
        console.log('Notification store initialized successfully');
      } catch (error) {
        console.error('Failed to initialize push notifications:', error);
        this.error = error.message;
        this.isInitialized = false;
      }
    },

    setupListeners() {
      pushNotificationService.addListener('registration', (token) => {
        this.registrationToken = token.value;
      });

      pushNotificationService.addListener('notificationReceived', (notification) => {
        this.notifications.unshift(notification);
      });

      pushNotificationService.addListener('notificationActionPerformed', ({ notification }) => {
        this.notifications.unshift(notification);
      });

      pushNotificationService.addListener('notificationRead', (notification) => {
        const index = this.notifications.findIndex(n => n.id === notification.id);
        if (index !== -1) {
          this.notifications[index] = { ...notification };
        }
      });

      pushNotificationService.addListener('notificationUnread', (notification) => {
        const index = this.notifications.findIndex(n => n.id === notification.id);
        if (index !== -1) {
          this.notifications[index] = { ...notification };
        }
      });

      pushNotificationService.addListener('notificationsCleared', () => {
        this.notifications = [];
      });
    },

    markAsRead(notificationId) {
      pushNotificationService.markNotificationAsRead(notificationId);
    },

    markAsUnread(notificationId) {
      pushNotificationService.markNotificationAsUnread(notificationId);
    },

    markAllAsRead() {
      this.notifications.forEach(notification => {
        if (notification.unread) {
          this.markAsRead(notification.id);
        }
      });
    },

    clearAllNotifications() {
      pushNotificationService.clearNotifications();
    },

    async requestPermissions() {
      try {
        const permissions = await pushNotificationService.requestPermissions();
        this.permissionStatus = permissions.receive;
        return permissions;
      } catch (error) {
        this.error = error.message;
        throw error;
      }
    },

    async checkPermissions() {
      try {
        const permissions = await pushNotificationService.checkPermissions();
        this.permissionStatus = permissions.receive;
        return permissions;
      } catch (error) {
        this.error = error.message;
        throw error;
      }
    },

    getRegistrationToken() {
      return this.registrationToken;
    },

    addMockNotification(notification) {
      const mockNotification = {
        id: Date.now().toString(),
        title: notification.title || 'Test Notification',
        body: notification.body || 'This is a test notification',
        data: notification.data || {},
        unread: true,
        timestamp: new Date().toISOString(),
        type: notification.type || 'System'
      };
      this.notifications.unshift(mockNotification);
    },

    clearError() {
      this.error = null;
    }
  },

  persist: {
    key: 'notification-store',
    storage: localStorage,
    paths: ['notifications', 'registrationToken', 'permissionStatus']
  }
});